<template>
  <div id="provider-profile">
    <provider-profile-error
      v-if="generalErrorType"
      :errorType="generalErrorType"
      :viewerAccountData="accountData"
      :paramData="paramData"
    />
    <div v-else>
      <provider-profile-banner v-if="bannerType" :bannerType="bannerType" @unhide-profile="unhideProfile" />
      <provider-profile-container
        :profileAccountData="profileAccountData"
        :viewerAccountData="accountData"
        :reviewsData="reviewsData"
        :loading="loading"
        :bannerType="bannerType"
        :paramData="paramData"
        :accountTypeInfo="accountTypeInfo"
        @view-more-reviews="handleViewMoreReviews"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { mapGetters } from 'vuex';
import { BannerType, GeneralErrorType } from './__types__/ErrorTypes';
import { FormattedCareType } from './__types__/CareTypes';
import ProviderProfileError from './components/ProviderProfileError.vue';
import ProviderProfileBanner from './components/ProviderProfileBanner.vue';
import ProviderProfileContainer from './components/ProviderProfileContainer.vue';
import { profileQueryMap, FETCH_REVIEWS, VIEWER_HAS_MESSAGED } from '@/graphql/provider/profile/queries';
import { UPDATE_SITTER_PROFILE } from '@/graphql/account/provider/mutations';

export default {
  name: 'ProviderProfile',
  components: {
    ProviderProfileError,
    ProviderProfileBanner,
    ProviderProfileContainer,
  },
  data(): any {
    return {
      generalErrorType: GeneralErrorType.NONE,
      bannerType: BannerType.NONE,
      profileAccountData: null,
      reviewsData: null,
      applicationsData: null,
      loading: true,
      reviewsFetchCount: 3,
    };
  },
  computed: {
    ...mapGetters('account', ['getAccount']),
    accountData() {
      return this.getAccount;
    },
    paramData() {
      return {
        careType: this.$route.params.care_type,
        sitterUuid: this.$route.params.sitter_uuid,
      };
    },
    accountTypeInfo() {
      return {
        isSitter: this.accountData?.isSitter?.() || false,
        isParent: this.accountData?.isParent?.() || false,
        accountType: this.accountData?.accountType?.() || '',
      };
    },
    formattedCareType() {
      return FormattedCareType[this.paramData.careType.toUpperCase()];
    },
    careTypeProfile() {
      return this.profileAccountData?.profiles?.[this.formattedCareType] || {};
    },
    isProfileOwner() {
      return this.profileAccountData?.id === this.accountData?.id;
    },
    showProfile() {
      return this.careTypeProfile?.showProfile;
    },
    careTypeDisplay() {
      return this.paramData.careType.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    },
  },
  watch: {
    '$route.params.care_type': {
      handler() {
        // Clear banner state when route changes to prevent flash of old banner
        this.bannerType = BannerType.NONE;
        this.generalErrorType = GeneralErrorType.NONE;
        this.fetchProfileData();
      },
    },
    '$route.params.sitter_uuid': {
      handler() {
        // Clear banner state when route changes to prevent flash of old banner
        this.bannerType = BannerType.NONE;
        this.generalErrorType = GeneralErrorType.NONE;
        this.fetchProfileData();
      },
    },
    'profileAccountData.id': 'updateBannerState',
    'profileAccountData.profiles': 'updateBannerState',
    'accountData.id': 'updateBannerState',
  },
  mounted() {
    this.fetchProfileData();
    this.fetchReviews();
    this.fetchViewerHasMessaged();
  },
  methods: {
    updateBannerState() {
      if (!this.profileAccountData) return;
      const { profiles } = this.profileAccountData;
      if (!profiles) return;

      const { profileVisible, profileCompleted } = this.careTypeProfile;

      // For owners, if profile is not completed, show incomplete error
      if (this.isProfileOwner && !profileCompleted && !profileVisible) {
        this.generalErrorType = GeneralErrorType.PROFILE_INCOMPLETE;
        this.bannerType = BannerType.NONE;
      } else if (this.isProfileOwner && !profileVisible) {
        // For owners, if profileVisible is false, show hidden banner
        this.bannerType = BannerType.PROFILE_HIDDEN;
      } else if (this.isProfileOwner && profileVisible && profileCompleted) {
        // For owners, if both profileVisible and profileCompleted are true, hide the banner
        this.bannerType = BannerType.NONE;
      } else if (!this.isProfileOwner && !this.showProfile) {
        // For non-owners, if profile is not visible, show hidden banner
        this.bannerType = BannerType.PROFILE_HIDDEN;
      } else {
        this.bannerType = BannerType.NONE;
      }
    },
    handleViewMoreReviews(): void {
      this.reviewsFetchCount += 3;
      this.fetchReviews();
    },
    async fetchProfileData() {
      this.loading = true;
      // Reset error state when starting a new fetch
      this.generalErrorType = GeneralErrorType.NONE;

      try {
        if (!this.validateCareTypeQuery(this.paramData.careType)) {
          return;
        }

        const data = await this.executeProfileQuery(this.paramData.careType);
        this.processProfileData(data);
      } catch (error) {
        this.handleProfileErrors(error);
      } finally {
        this.loading = false;
      }
    },
    validateCareTypeQuery(careType: string): boolean {
      const careTypeQuery = profileQueryMap[careType.toLowerCase()];

      if (!careTypeQuery) {
        this.generalErrorType = GeneralErrorType.DEFAULT;
        console.error(`No query found for care type: ${careType}`); // eslint-disable-line no-console
        return false;
      }

      return true;
    },
    async executeProfileQuery(careType: string) {
      const careTypeQuery = profileQueryMap[careType.toLowerCase()];

      const { data } = await this.$apollo.query({
        query: careTypeQuery,
        variables: {
          accountId: this.paramData.sitterUuid,
        },
      });

      return data;
    },
    processProfileData(data: any) {
      this.profileAccountData = { ...this.profileAccountData, ...data.account };

      if (!this.validateProfileExists(this.careTypeProfile)) {
        return;
      }

      if (!this.validateProfileVisibility(this.careTypeProfile, this.isProfileOwner)) {
        return;
      }

      this.setProfileStates(this.careTypeProfile, this.isProfileOwner);
    },
    validateProfileExists(careTypeProfile: any): boolean {
      // A profile is considered unavailable if it does not exist, the profile object is missing, or the bio is missing
      if (!careTypeProfile || !careTypeProfile.profile || !careTypeProfile.profile.bio) {
        this.generalErrorType = GeneralErrorType.PROFILE_UNAVAILABLE;
        this.bannerType = BannerType.NONE;
        return false;
      }
      return true;
    },
    validateProfileVisibility(careTypeProfile: any, isProfileOwner: boolean): boolean {
      // For non-owners, if showProfile is false, show error
      if (!isProfileOwner && !careTypeProfile.showProfile) {
        this.generalErrorType = GeneralErrorType.PROFILE_UNAVAILABLE;
        this.bannerType = BannerType.NONE;
        return false;
      }
      return true;
    },
    setProfileStates(careTypeProfile: any, isProfileOwner: boolean) {
      // For owners, if profile is not completed, show incomplete error
      if (isProfileOwner && !careTypeProfile.profileCompleted) {
        this.generalErrorType = GeneralErrorType.PROFILE_INCOMPLETE;
        this.bannerType = BannerType.NONE;
      } else if (isProfileOwner && (!careTypeProfile.profileVisible || !careTypeProfile.profileCompleted)) {
        // For owners, if profileVisible is false OR profileCompleted is false, show hidden banner
        this.bannerType = BannerType.PROFILE_HIDDEN;
      } else if (isProfileOwner && careTypeProfile.profileVisible && careTypeProfile.profileCompleted) {
        // For owners, if both profileVisible and profileCompleted are true, hide the banner
        this.bannerType = BannerType.NONE;
      }
    },
    handleProfileErrors(error: any) {
      console.error(error); // eslint-disable-line no-console

      // Show "profile unavailable" error if the account is not found (e.g. closed, termed)
      if (error.message.includes('Account not found')) {
        this.generalErrorType = GeneralErrorType.PROFILE_UNAVAILABLE;
        return;
      }

      // Show default error for any other errors
      this.generalErrorType = GeneralErrorType.DEFAULT;
    },
    async fetchViewerHasMessaged() {
      try {
        const { data } = await this.$apollo.query({
          query: VIEWER_HAS_MESSAGED,
          variables: {
            accountId: this.paramData.sitterUuid,
          },
        });
        this.profileAccountData = {
          ...this.profileAccountData,
          viewerHasMessaged: data.account.viewerHasMessaged,
        };
        return data.account.viewerHasMessaged;
      } catch (error) {
        console.error('Error fetching viewer has messaged:', error); // eslint-disable-line no-console
        return false;
      }
    },
    fetchReviews() {
      this.$apollo
        .query({
          query: FETCH_REVIEWS,
          variables: {
            accountId: this.paramData.sitterUuid,
            reviewsFetchCount: this.reviewsFetchCount,
          },
        })
        .then(({ data }) => {
          this.reviewsData = data.account.reviews;
        })
        .catch((error) => {
          console.error('Error fetching reviews:', error); // eslint-disable-line no-console
        });
    },
    async unhideProfile() {
      const { careType } = this.paramData;
      const originalBannerType = this.bannerType;

      try {
        // Optimistically update the banner state for immediate feedback
        this.bannerType = BannerType.NONE;

        await this.$apollo.mutate({
          mutation: UPDATE_SITTER_PROFILE,
          variables: {
            input: {
              sitterToggleProfile: {
                careType: careType.toUpperCase(),
              },
            },
          },
        });
      } catch (error) {
        // Revert banner state on error
        this.bannerType = originalBannerType;
        console.error('Error unhiding profile:', error);

        // Show user-friendly error message
        this.$toast?.error?.('Failed to unhide profile. Please try again.');
      }
    },
  },
};
</script>

<style lang="scss">
main:has(#provider-profile) ~ #onetrust-consent-sdk #ot-sdk-btn-floating {
  z-index: 1;
}
</style>
